import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Clock, PiggyBank, TrendingUp } from "lucide-react";

const METHODS = [
  { id: "manual", label: "Spreadsheets & email", hoursPerTraveler: 4.2, leakRate: 0.11 },
  { id: "legacy", label: "Legacy expense tool", hoursPerTraveler: 2.1, leakRate: 0.06 },
];

const fmt = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

export const RoiCalculator = () => {
  const [employees, setEmployees] = useState(250);
  const [spend, setSpend] = useState(1200);
  const [method, setMethod] = useState("manual");

  const result = useMemo(() => {
    const m = METHODS.find((x) => x.id === method);
    const hoursSaved = Math.round(employees * m.hoursPerTraveler);
    const monthlySavings = Math.round(employees * spend * m.leakRate);
    const roi = ((monthlySavings + hoursSaved * 40) / Math.max(1, employees * 8)).toFixed(1);
    return { hoursSaved, monthlySavings, roi };
  }, [employees, spend, method]);

  return (
    <section id="roi" data-testid="roi-section" className="bg-[#090d16] noise-bg py-24 lg:py-32 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="font-mono text-[11px] tracking-widest text-blue-400 uppercase">Savings calculator</p>
          <h2 className="mt-4 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl">
            What is expense leakage costing you?
          </h2>
          <p className="mt-4 text-slate-400 text-base md:text-lg max-w-lg">
            Manual expense handling leaks 8–12% of travel spend through out-of-policy claims, duplicate receipts, and idle approval queues. Move the sliders to size your gap.
          </p>

          <div className="mt-12 space-y-10">
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-semibold text-slate-200">Traveling employees</label>
                <span data-testid="roi-employees-value" className="font-mono text-sm text-blue-400">{fmt(employees)}</span>
              </div>
              <div data-testid="roi-calculator-employees-slider">
                <Slider
                  value={[employees]}
                  onValueChange={([v]) => setEmployees(v)}
                  min={10}
                  max={2000}
                  step={10}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-semibold text-slate-200">Avg monthly spend per traveler</label>
                <span data-testid="roi-spend-value" className="font-mono text-sm text-blue-400">${fmt(spend)}</span>
              </div>
              <Slider
                data-testid="roi-calculator-spend-input"
                value={[spend]}
                onValueChange={([v]) => setSpend(v)}
                min={200}
                max={5000}
                step={50}
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-200 block mb-3">Current process</label>
              <div className="flex gap-2">
                {METHODS.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`roi-method-${m.id}-button`}
                    onClick={() => setMethod(m.id)}
                    className={`px-4 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 active:scale-95 ${
                      method === m.id
                        ? "bg-blue-600 text-white"
                        : "border border-white/15 text-slate-300 hover:bg-white/5"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-5">
          {[
            { icon: Clock, testid: "roi-hours-output", value: `${fmt(result.hoursSaved)} hrs`, label: "Processing hours saved per month", sub: "Across employees, managers & finance" },
            { icon: PiggyBank, testid: "roi-savings-output", value: `$${fmt(result.monthlySavings)}`, label: "Direct policy savings per month", sub: "Recovered from leakage & duplicate claims" },
            { icon: TrendingUp, testid: "roi-multiplier-output", value: `${result.roi}x`, label: "Projected ROI multiplier", sub: "Versus CTEMS platform cost" },
          ].map((r) => (
            <div
              key={r.testid}
              data-testid={r.testid}
              className="rounded-xl border border-white/10 bg-slate-900/60 p-7 flex items-center gap-6 hover:border-emerald-500/40 transition-colors duration-300"
            >
              <span className="w-12 h-12 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
                <r.icon className="w-5 h-5" />
              </span>
              <div>
                <p className="font-heading font-extrabold text-3xl text-white">{r.value}</p>
                <p className="text-sm font-semibold text-slate-300 mt-1">{r.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{r.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
